/**
 * ClientRecordEditor — Renders any vertical record from `template.modules_schema`.
 * Phase 2: fully generic — no per-sector code. New sectors only add JSON.
 *
 * Schema:
 *   template.modules_schema = [
 *     { key, kind: "fields"|"table", label_it, label_en, icon, fields?: [...], columns?: [...] }
 *   ]
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "../../App";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import {
  Save, Loader2, Plus, Trash2, ClipboardList, Pill, Stethoscope, FileSignature,
  Activity, Sparkles, Scissors, Hand, Bone, Heart, Image as ImageIcon, Package,
  Repeat, Wand2, Palette, History as HistoryIcon, Dumbbell, ListChecks,
  TrendingUp, Gift, ClipboardCheck,
} from "lucide-react";
import { toast } from "sonner";

// String → Lucide component map. Keep in sync with backend `icon` field.
const ICONS = {
  ClipboardList, Pill, Stethoscope, FileSignature,
  Activity, Sparkles, Scissors, Hand, Bone, Heart, Package, Repeat, Wand2,
  Palette, Dumbbell, ListChecks, TrendingUp, Gift, ClipboardCheck,
  Image: ImageIcon, History: HistoryIcon,
};

const iconFor = (name) => ICONS[name] || ClipboardList;

const ClientRecordEditor = ({ clientId, template, record, onSaved, t }) => {
  const [data, setData] = useState({});
  const [saving, setSaving] = useState(false);
  const [active, setActive] = useState(template?.modules_schema?.[0]?.key || "");
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setData((record?.dynamic_data) || {});
    setDirty(false);
  }, [record]);

  // Use modules_schema directly (Phase 2 generic). Fallback for legacy templates.
  const modules = useMemo(() => template?.modules_schema || [], [template]);

  useEffect(() => {
    if (!active && modules[0]) setActive(modules[0].key);
  }, [modules, active]);

  const setSection = (key, value) => {
    setData(prev => ({ ...prev, [key]: value }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/clients/${clientId}/record/${template.sector_key}`,
        { dynamic_data: data }, { withCredentials: true });
      toast.success(t("Scheda salvata", "Card saved"));
      setDirty(false);
      if (onSaved) onSaved();
    } catch {
      toast.error(t("Errore salvataggio", "Save error"));
    } finally {
      setSaving(false);
    }
  };

  if (!template) return null;

  const TemplateIcon = iconFor(template.icon);

  return (
    <div className="rounded-2xl border border-slate-200/60 dark:border-white/10 bg-white dark:bg-slate-900/40 overflow-hidden" data-testid="client-record-editor">
      {/* Header */}
      <div className={`px-4 py-3 border-b border-slate-100 dark:border-white/5 flex items-center justify-between gap-3 bg-gradient-to-r ${template.color || "from-violet-500 to-cyan-500"} bg-opacity-10`}>
        <div className="flex items-center gap-2.5">
          <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${template.color || "from-violet-500 to-cyan-500"} flex items-center justify-center text-white shadow`}>
            <TemplateIcon className="w-4 h-4" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-slate-900 dark:text-white">{template.name}</h3>
            <p className="text-[10px] text-muted-foreground">
              {template.tagline || t("Scheda operativa", "Operational card")} · v{template.version}
            </p>
          </div>
        </div>
        <Button size="sm" className="rounded-full" onClick={save} disabled={saving || !dirty}
                data-testid="client-record-save-btn">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Save className="w-3.5 h-3.5 mr-1" />{t("Salva scheda", "Save card")}</>}
        </Button>
      </div>

      {/* Module sub-tabs */}
      <div className="flex gap-1 px-3 py-2 border-b border-slate-100 dark:border-white/5 bg-slate-50/50 dark:bg-slate-900/30 overflow-x-auto" data-testid="record-modules-nav">
        {modules.map(m => {
          const Icon = iconFor(m.icon);
          const isActive = active === m.key;
          return (
            <button
              key={m.key}
              onClick={() => setActive(m.key)}
              className={`flex-shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold transition-all ${
                isActive
                  ? "bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              data-testid={`record-module-${m.key}`}
            >
              <Icon className="w-3 h-3" /> {t(m.label_it, m.label_en)}
            </button>
          );
        })}
      </div>

      {/* Body */}
      <div className="p-4">
        {modules.map(m => {
          if (m.key !== active) return null;
          if (m.kind === "fields") {
            return (
              <FieldsSection
                key={m.key}
                fields={m.fields || []}
                value={data[m.key] || {}}
                onChange={v => setSection(m.key, v)}
                t={t}
              />
            );
          }
          // Table
          return (
            <TableSection
              key={m.key}
              cols={m.columns || []}
              rows={data[m.key] || []}
              onChange={rows => setSection(m.key, rows)}
              t={t}
            />
          );
        })}
      </div>
    </div>
  );
};

/* ---------- Fields renderer ---------- */
const FieldsSection = ({ fields, value, onChange, t }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
    {fields.map(f => {
      const v = value[f.key];
      const wrap = f.type === "textarea" ? "sm:col-span-2" : "";
      if (f.type === "boolean") {
        return (
          <div key={f.key} className="flex items-center justify-between rounded-xl border border-slate-200/60 dark:border-white/10 px-3 py-2 hover:border-violet-300 transition-colors">
            <span className="text-xs font-medium">{f.label}</span>
            <Switch checked={!!v} onCheckedChange={(b) => onChange({ ...value, [f.key]: b })} />
          </div>
        );
      }
      if (f.type === "textarea") {
        return (
          <div key={f.key} className={wrap}>
            <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">{f.label}</label>
            <Textarea
              value={v || ""}
              onChange={e => onChange({ ...value, [f.key]: e.target.value })}
              className="text-xs min-h-[60px]"
              data-testid={`record-field-${f.key}`}
            />
          </div>
        );
      }
      if (f.type === "select") {
        return (
          <div key={f.key}>
            <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">{f.label}</label>
            <Select value={v || ""} onValueChange={val => onChange({ ...value, [f.key]: val })}>
              <SelectTrigger className="text-xs h-9" data-testid={`record-field-${f.key}`}><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                {(f.options || []).map(o => <SelectItem key={o} value={o}>{o.replace(/_/g, " ")}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        );
      }
      return (
        <div key={f.key}>
          <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">{f.label}</label>
          <Input
            type={f.type === "number" || f.type === "currency" ? "number" : f.type === "date" ? "date" : "text"}
            step={f.type === "currency" ? "0.01" : undefined}
            value={v ?? ""}
            onChange={e => onChange({ ...value, [f.key]: f.type === "number" || f.type === "currency" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value })}
            className="text-xs h-9"
            data-testid={`record-field-${f.key}`}
          />
        </div>
      );
    })}
  </div>
);

/* ---------- Table renderer ---------- */
const TableSection = ({ cols, rows, onChange, t }) => {
  const addRow = () => {
    const empty = cols.reduce((acc, c) => ({ ...acc, [c.key]: c.type === "boolean" ? false : "" }), {});
    onChange([...(rows || []), empty]);
  };
  const updateRow = (i, key, val) => {
    const next = rows.map((r, idx) => idx === i ? { ...r, [key]: val } : r);
    onChange(next);
  };
  const removeRow = (i) => onChange(rows.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-2" data-testid="record-table-section">
      {(rows || []).length === 0 && (
        <p className="text-xs text-muted-foreground text-center py-6">{t("Nessuna voce. Aggiungine una qui sotto.", "No items yet. Add one below.")}</p>
      )}
      {(rows || []).map((r, i) => (
        <div key={i} className="rounded-xl border border-slate-200/60 dark:border-white/10 p-3 grid grid-cols-1 md:grid-cols-2 gap-2 hover:border-violet-300 transition-colors">
          {cols.map(col => {
            const v = r[col.key];
            const wrap = col.type === "textarea" ? "md:col-span-2" : "";
            if (col.type === "select") {
              return (
                <div key={col.key} className={wrap}>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{col.label}</label>
                  <Select value={v || ""} onValueChange={val => updateRow(i, col.key, val)}>
                    <SelectTrigger className="text-xs h-8 mt-0.5"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      {(col.options || []).map(o => <SelectItem key={o} value={o}>{o.replace(/_/g, " ")}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              );
            }
            if (col.type === "textarea") {
              return (
                <div key={col.key} className={wrap}>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{col.label}</label>
                  <Textarea value={v || ""} onChange={e => updateRow(i, col.key, e.target.value)}
                            className="text-xs min-h-[50px] mt-0.5" />
                </div>
              );
            }
            if (col.type === "boolean") {
              return (
                <div key={col.key} className="flex items-center justify-between rounded-lg border border-slate-200 dark:border-white/10 px-2 py-1.5">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{col.label}</span>
                  <Switch checked={!!v} onCheckedChange={(b) => updateRow(i, col.key, b)} />
                </div>
              );
            }
            return (
              <div key={col.key}>
                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{col.label}</label>
                <Input
                  type={col.type === "number" || col.type === "currency" ? "number" : col.type === "date" ? "date" : "text"}
                  step={col.type === "currency" ? "0.01" : undefined}
                  value={v ?? ""}
                  onChange={e => updateRow(i, col.key, col.type === "number" || col.type === "currency" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)}
                  className="text-xs h-8 mt-0.5"
                />
              </div>
            );
          })}
          <button onClick={() => removeRow(i)}
                  className="md:col-span-2 text-[11px] text-red-500 hover:text-red-700 inline-flex items-center gap-1 mt-1 self-end justify-self-end"
                  data-testid={`record-row-remove-${i}`}>
            <Trash2 className="w-3 h-3" /> {t("Rimuovi", "Remove")}
          </button>
        </div>
      ))}
      <Button size="sm" variant="outline" className="rounded-full w-full h-9" onClick={addRow}
              data-testid="record-row-add-btn">
        <Plus className="w-3.5 h-3.5 mr-1" /> {t("Aggiungi voce", "Add row")}
      </Button>
    </div>
  );
};

export default ClientRecordEditor;
